/**
 * Site-header interactivity: menu drawer + sticky-state. (Site search was
 * removed from the headers; the search overlay went with it.)
 *
 * Markup contract (see patterns/header.php):
 *   .cwp-site-header
 *     button.cwp-nav-toggle        — opens drawer
 *   #cwp-nav-drawer + .cwp-nav-drawer__close
 *   .cwp-nav-backdrop              — shared backdrop for the drawer
 *
 * Open/close animation is class-based (.is-open) rather than `hidden`
 * attribute toggling, so the slide-in and the backdrop fade actually
 * play. The drawer's CSS keeps it transform: translateX(100%) by
 * default and JS just flips a class.
 */
export const cwpNav = {
	init() {
		const header = document.querySelector('.cwp-site-header');

		if (!header) {
			return;
		}

		this._initDrawer(header);
		this._initStickyState(header);
	},

	_initDrawer(header) {
		const toggle = header.querySelector('.cwp-nav-toggle');
		const drawer = document.getElementById('cwp-nav-drawer');
		const backdrop = document.querySelector('.cwp-nav-backdrop');
		const closeBtn = drawer?.querySelector('.cwp-nav-drawer__close');

		if (!toggle || !drawer || !backdrop) {
			return;
		}

		// Pre-JS the drawer + backdrop start with `hidden` so they don't
		// flash. Remove it now — visibility is controlled by .is-open
		// + the CSS transitions on transform/opacity.
		drawer.removeAttribute('hidden');
		backdrop.removeAttribute('hidden');

		const open = () => {
			drawer.classList.add('is-open');
			backdrop.classList.add('is-open');
			document.body.classList.add('cwp-nav-open');
			toggle.setAttribute('aria-expanded', 'true');
			// Focus after the slide-in finishes so the focus ring doesn't
			// fire from off-screen.
			window.setTimeout(() => closeBtn?.focus(), 220);
		};

		const close = () => {
			drawer.classList.remove('is-open');
			backdrop.classList.remove('is-open');
			document.body.classList.remove('cwp-nav-open');
			toggle.setAttribute('aria-expanded', 'false');
			toggle.focus();
		};

		toggle.addEventListener('click', () => {
			drawer.classList.contains('is-open') ? close() : open();
		});

		closeBtn?.addEventListener('click', close);
		backdrop.addEventListener('click', close);

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
				close();
			}
		});

		drawer.querySelectorAll('a').forEach((link) => {
			link.addEventListener('click', close);
		});
	},

	/**
	 * From 900px the header's template part sticks with the logo row parked
	 * above the window (frontend.scss, "What stays on screen"). Measure what
	 * the CSS needs: the logo row's height (--cwp-header-hide) and what stays
	 * (--cwp-header-stuck-h). Toggle .is-stuck, for the shadow, while the menu
	 * row floats over the page; never where the part does not stick.
	 */
	_initStickyState(header) {
		const part = header.parentElement;
		const row = header.querySelector('.cwp-site-header__nav-row');
		let ticking = false;

		const measure = () => {
			if (!row) return;
			const h = header.getBoundingClientRect();
			const r = row.getBoundingClientRect();
			document.documentElement.style.setProperty('--cwp-header-hide', `${r.top - h.top}px`);
			document.documentElement.style.setProperty('--cwp-header-stuck-h', `${h.bottom - r.top}px`);
		};

		const update = () => {
			ticking = false;
			// top is 'auto' (NaN, never stuck) where the part does not stick.
			const top = parseFloat(getComputedStyle(part).top);
			header.classList.toggle('is-stuck', part.getBoundingClientRect().top <= top + 0.5);
		};

		window.addEventListener(
			'scroll',
			() => {
				if (!ticking) {
					window.requestAnimationFrame(update);
					ticking = true;
				}
			},
			{ passive: true }
		);

		// Runs once right away (the initial state, also for a restored scroll),
		// then whenever the logo, a font or the 900px breakpoint resizes it.
		new ResizeObserver(() => {
			measure();
			update();
		}).observe(header);
	},
};
